import { TAuditChangeLog } from "change-events/change-event-polling";
import { RiskStates } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getMaterialsReport, getSubmittalsReport } from "services/auth";

export const useCiqFeatureSubmittalRiskReportList = ({
  risk_level
}: {
  risk_level?: RiskStates;
}): {
  gridData: {
    submittals?: any;
  };
} => {
  const [gridData, setGridData] = useState<{
    submittals?: any;
  }>({
    submittals: null
  });
  const { eventLogs, tokenRetrievalState } = useContext(ProjectContext);

  useEffect(() => {
    const getGridData = async () => {
      const data = await getSubmittalsReport(
        tokenRetrievalState.token,
        risk_level
      );
      if (data.success && data.data) {
        setGridData({
          submittals: data.data.success
        });
      }
    };

    if (tokenRetrievalState.token) {
      getGridData();
    }
  }, [tokenRetrievalState.token, risk_level]);

  const deleteSubmittalsFromList = useCallback(
    (ids: Array<string>) => {
      const items = gridData.submittals || [];

      ids.forEach((id) => {
        const index = items.findIndex((x: any) => x.id === id);
        items.splice(index, 1);
      });
      setGridData({
        submittals: items
      });
    },
    [gridData.submittals]
  );

  const getAndUpdateSubmittalList = useCallback(
    async (submittalIds: Array<string>) => {
      if (!submittalIds.length) return;

      const res = await getSubmittalsReport(
        tokenRetrievalState.token,
        risk_level,
        submittalIds
      );
      if (res.success && res.data) {
        const updatedSubmittalList = res?.data?.data?.success || [];

        if (updatedSubmittalList.length === 0) {
          deleteSubmittalsFromList(submittalIds);
          return;
        }

        const items = [...gridData.submittals] as Array<any>;
        updatedSubmittalList.forEach((item: any) => {
          const foundIndex = items.findIndex((x) => x.id === item.id);
          if (foundIndex === -1) {
            items.push(item);
          } else {
            items[foundIndex] = item;
          }
        });
        setGridData({
          submittals: items
        });
      }
    },
    [
      tokenRetrievalState.token,
      risk_level,
      gridData.submittals,
      deleteSubmittalsFromList
    ]
  );

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      const featureLogs = eventLogs.filter(
        (log) =>
          (log.info?.feature_type === "SUBMITTALS" ||
            log.info?.feature_type_ids?.includes(FeatureTypes.SUBMITTALS)) &&
          log.data_source === "feature_instance"
      );

      if (featureLogs.length) {
        const deletedIds = featureLogs
          .filter((log) => ["delete", "bulk_delete"].includes(log.change_type))
          .map((s: TAuditChangeLog) => s.info.feature_instance_ids) as any;
        if (deletedIds.length) {
          const deletedDArray = [...new Set([].concat([], ...deletedIds))];
          deleteSubmittalsFromList(deletedDArray);
        }

        const ids = featureLogs
          .filter((log) => !["delete", "bulk_delete"].includes(log.change_type)) // update create import
          .map((s) => s.info.feature_instance_ids) as any;

        if (ids.length) {
          const singleDArray = [...new Set([].concat([], ...ids))];
          getAndUpdateSubmittalList(singleDArray);
        }
      }
    }
    previousEventLogs.current = eventLogs;
  }, [deleteSubmittalsFromList, eventLogs, getAndUpdateSubmittalList]);

  return { gridData };
};

export const useCiqFeatureMaterialRiskReportList = ({
  risk_level
}: {
  risk_level?: RiskStates;
}): {
  gridData: {
    materials?: any;
  };
} => {
  const { eventLogs, tokenRetrievalState } = useContext(ProjectContext);

  const [gridData, setGridData] = useState<{
    materials?: any;
  }>({
    materials: null
  });

  useEffect(() => {
    const getGridData = async () => {
      const data = await getMaterialsReport(
        tokenRetrievalState.token,
        risk_level
      );
      if (data.success && data.data) {
        setGridData({
          materials: data.data.success
        });
      }
    };

    if (tokenRetrievalState.token) {
      getGridData();
    }
  }, [risk_level, tokenRetrievalState.token]);

  const deleteMaterialsFromList = useCallback(
    (ids: Array<string>) => {
      const items = gridData.materials ? [...gridData.materials] : [];
      ids.forEach((id) => {
        const index = items.findIndex((x) => x.id === id);
        items.splice(index, 1);
      });
      setGridData({
        materials: items
      });
    },
    [gridData.materials]
  );

  const getAndUpdateMaterialList = useCallback(
    async (materialIds: Array<string>) => {
      if (!materialIds.length) return;
      const res = await getMaterialsReport(
        tokenRetrievalState.token,
        risk_level,
        materialIds
      );
      if (res.success && res.data) {
        const updatedmaterialList = res?.data?.data?.success || [];

        if (updatedmaterialList.length === 0) {
          deleteMaterialsFromList(materialIds);
          return;
        }

        const items = [...gridData.materials] as Array<any>;
        updatedmaterialList.forEach((item: any) => {
          const foundIndex = items.findIndex((x) => x.id === item.id);
          if (foundIndex === -1) {
            items.push(item);
          } else {
            items[foundIndex] = item;
          }
        });
        setGridData({
          materials: items
        });
      }
    },
    [
      deleteMaterialsFromList,
      gridData.materials,
      risk_level,
      tokenRetrievalState.token
    ]
  );

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      const featureLogs = eventLogs.filter(
        (log) =>
          (log.info?.feature_type === "MATERIALS" ||
            log.info?.feature_type_ids?.includes(FeatureTypes.MATERIALS)) &&
          log.data_source === "feature_instance"
      );

      if (featureLogs.length) {
        const deletedIds = featureLogs
          .filter((log) => ["delete", "bulk_delete"].includes(log.change_type))
          .map((s: TAuditChangeLog) => s.info.feature_instance_ids) as any;
        if (deletedIds.length) {
          const deletedDArray = [...new Set([].concat([], ...deletedIds))];
          deleteMaterialsFromList(deletedDArray);
        }

        const ids = featureLogs
          .filter((log) => !["delete", "bulk_delete"].includes(log.change_type)) // update create import
          .map((s) => s.info.feature_instance_ids) as any;

        if (ids.length) {
          const singleDArray = [...new Set([].concat([], ...ids))];
          getAndUpdateMaterialList(singleDArray);
        }
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, deleteMaterialsFromList, getAndUpdateMaterialList]);

  return { gridData };
};
